/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import axios from "axios";
import {
  Box,
  Typography,
  List,
  Avatar,
  Card,
  CardContent,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Drawer,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Divider,
  Badge
} from '@mui/material';
import {
  Home as HomeIcon,
  Payment as PaymentIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  EventNote as EventNoteIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Inbox as InboxIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon

} from "@mui/icons-material";
import MyProfileDialog from './components/common/MyProfileDialog';
import { formatTime12h, formatDateTimeToTime12h, formatDateOnly as formatManilaDateOnly, toDateKey, getCurrentDateKey } from './utils/dateTimeUtils';
import { formatDatePH, formatDateTimePH } from './utils/dateFormat';
import PayslipCard from './components/PayslipCard';
import LeaveRequestForm from './components/Leave/LeaveRequestForm';
import LeaveBalancesSummary from './components/Leave/LeaveBalancesSummary';
import LeaveRequestsTable from './components/Leave/LeaveRequestsTable';
import InboxPanel from './components/InboxPanel';
import SidebarNavItem from './components/Layout/SidebarNavItem';
import PersonalCalendar from './components/Calendar/PersonalCalendar';
import LiveDateTimeWidget from './components/LiveDateTimeWidget';
import { formatName } from './utils/formatName';
import { fetchPayslipRow } from './utils/payslipApi';
import { API_BASE } from './utils/apiBase';
import { cleanStatus } from './utils/attendanceUi';

const INVENTORY_WARNING_STOCK_THRESHOLD = 70;
const INVENTORY_CRITICAL_STOCK_THRESHOLD = 30;
const getInventoryStockLevel = (quantity) => {
  const qty = Number(quantity || 0);
  if (qty < INVENTORY_CRITICAL_STOCK_THRESHOLD) return 'critical';
  if (qty <= INVENTORY_WARNING_STOCK_THRESHOLD) return 'warning';
  return 'ok';
};

function EmployeeDashboard({ user, onLogout }) {
  const getStatusChip = (statusLabel) => {
    const cleaned = cleanStatus(statusLabel);
    const s = cleaned.toLowerCase();
    let sx = { fontWeight: 700 };
    if (s.includes('pending')) sx = { ...sx, bgcolor: '#f57c00', color: '#fff' };
    else if (s.includes('present')) sx = { ...sx, bgcolor: '#2e7d32', color: '#fff' };
    else if (s.includes('half day')) sx = { ...sx, bgcolor: '#8b5cf6', color: '#fff' };
    else if (s.includes('late')) sx = { ...sx, bgcolor: '#fbc02d', color: '#1f2937' };
    else if (s === 'absent') sx = { ...sx, bgcolor: '#d32f2f', color: '#fff' };
    else if (s.includes('leave')) sx = { ...sx, bgcolor: '#1976d2', color: '#fff' };
    else sx = { ...sx, bgcolor: '#64748b', color: '#fff' };
    return <Chip size="small" label={cleaned || '-'} sx={sx} />;
  };
  const getAttendanceStatusLabel = (row) => {
    const lateMinutes = Number(row?.late_minutes || row?.lateMinutes || 0) || 0;
    const lateLabel = lateMinutes > 0 ? `Late (${lateMinutes} min)` : 'Late';
    const explicitStatus = cleanStatus(row?.attendance_status_label || row?.status_label || '');
    if (explicitStatus.toLowerCase().includes('late')) return lateLabel;
    if (explicitStatus) return explicitStatus;
    const manualStatus = String(row?.manual_request_status || '').trim().toLowerCase();
    const manualResolution = String(row?.manual_request_resolution || '').trim().toLowerCase();
    if (manualStatus === 'pending') return 'Pending Manual Review';
    if (manualStatus === 'approved') return 'Present';
    if (manualStatus === 'rejected' && manualResolution === 'late') return lateLabel;
    if (manualStatus === 'rejected' && manualResolution === 'absent') return 'Absent';
    const leaveWorkflowStatus = String(row?.leave_request_status || '').trim().toLowerCase();
    const leavePaid = Number(row?.leave_request_paid || 0) === 1;
    if (leaveWorkflowStatus === 'pending') return leavePaid ? 'Pending Paid Leave' : 'Pending Unpaid Leave';
    if (leaveWorkflowStatus === 'approved') return leavePaid ? 'Paid Leave' : 'Unpaid Leave';
    if (row?.leave_approved) return 'Leave';
    const isAbsent = row?.is_absent === 1 || row?.is_absent === true;
    if (isAbsent) return 'Absent';
    if (row?.half_day === 1 || row?.half_day === true) return 'Half Day';
    if (lateMinutes > 0 || row?.late === 1 || row?.late === true || row?.late === '1') return lateLabel;
    if (row?.log_time || row?.time_in || row?.timeIn || row?.time_out || row?.timeOut) return 'Present';
    const cleaned = cleanStatus(row?.status || row?.attendance_status || '');
    if (cleaned.toLowerCase() === 'absent') return 'Absent';
    if (cleaned.toLowerCase() === 'late') return lateLabel;
    if (cleaned.toLowerCase() === 'present') return 'Present';
    return '';
  };
  const getAttendanceMethodLabel = (row) => {
    const source = String(row?.attendance_source || '').trim().toLowerCase();
    const kioskId = String(row?.kiosk_id || '').trim();
    const isFace = row?.face_recognized === 1 || row?.face_recognized === true || source.includes('face') || source.includes('kiosk') || Boolean(kioskId);
    if (isFace) return 'Face';
    const explicitMethod = String(row?.attendance_method_label || row?.method || '').trim();
    if (explicitMethod) return cleanStatus(explicitMethod);
    const status = String(getAttendanceStatusLabel(row) || '').toLowerCase();
    if (status.includes('leave')) return 'Leave Request';
    const hasAttendance = !!(row?.log_time || row?.time_in || row?.timeIn || row?.time_out || row?.timeOut || row?.is_absent || row?.manual_request_status);
    return hasAttendance ? 'Manual' : '-';
  };
  const mustChangePassword = user?.must_change_password === 1 || user?.must_change_password === true;
  const normalizedRole = (user?.role || '').toLowerCase();
  const fullDisplayName = formatName(user, { lastFirst: false, middle: 'full', capitalize: true }) || user?.username || '';
  const hasLeaveHistoryIdentity = Boolean(user?.id || user?.employeeid || user?.employee_no || user?.username);
  const drawerWidth = 260;

  const [activeSection, setActiveSection] = useState("home");
  const [mobileOpen, setMobileOpen] = useState(false);

  // State for leave requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveError, setLeaveError] = useState(null);
  const [allowedLeaveTypes, setAllowedLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState({});
  const allowedTypesByKey = React.useMemo(() => {
    const map = {};
    (allowedLeaveTypes || []).forEach((t) => {
      const key = t.key || t.code || t.value || '';
      if (key) map[String(key)] = t;
    });
    return map;
  }, [allowedLeaveTypes]);
  const handleLeaveMetaLoaded = React.useCallback((meta = {}) => {
    setAllowedLeaveTypes(Array.isArray(meta?.types) ? meta.types : []);
    setLeaveBalances(meta?.balances && typeof meta.balances === 'object' ? meta.balances : {});
  }, []);

  // State for inventory requests
  const [inventoryRequests, setInventoryRequests] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);
  // Inventory items (quantities)
  const [inventoryItems, setInventoryItems] = useState([]);
  // Notifications (Inbox)
  const [unreadInboxCount, setUnreadInboxCount] = useState(0);
  // Inventory request form state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addItemId, setAddItemId] = useState("");
  const [addQuantity, setAddQuantity] = useState(1);
  const [addMessage, setAddMessage] = useState("");
  const [addError, setAddError] = useState("");
  const [newItemName, setNewItemName] = useState('');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [openProfileDialog, setOpenProfileDialog] = useState(false);
  const [dtrPhotos, setDtrPhotos] = useState([]);
  const [dtrPhotosLoading, setDtrPhotosLoading] = useState(false);
  const [dtrPhotosError, setDtrPhotosError] = useState('');
  const [dtrReviewDialog, setDtrReviewDialog] = useState({ open: false, row: null, status: '', notes: '', saving: false, error: '' });
  const [companyAttendanceRows, setCompanyAttendanceRows] = useState([]);
  const [companyAttendanceLoading, setCompanyAttendanceLoading] = useState(false);
  const [companyAttendanceError, setCompanyAttendanceError] = useState('');
  const [dtrCompareDialog, setDtrCompareDialog] = useState({ open: false, row: null, rows: [], loading: false, error: '', month: null, year: null });
  const [companyAttendanceFilters, setCompanyAttendanceFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    role: 'all',
    status: 'all',
    method: 'all',
    employeeId: '',
  });
  const inventoryWarnings = React.useMemo(() => {
    return (inventoryItems || []).reduce((acc, item) => {
      const quantity = Number(item?.quantity || 0);
      const level = getInventoryStockLevel(quantity);
      if (level === 'critical') acc.outOfStockItems.push(item);
      else if (level === 'warning') acc.lowStockItems.push(item);
      return acc;
    }, { lowStockItems: [], outOfStockItems: [] });
  }, [inventoryItems]);
  const lowestInventoryItems = React.useMemo(() => (
    (inventoryItems || [])
      .map((item) => ({ ...item, quantity: Number(item?.quantity || 0) }))
      .filter((item) => item.quantity <= INVENTORY_WARNING_STOCK_THRESHOLD)
      .sort((a, b) => a.quantity - b.quantity || String(a.item_name || '').localeCompare(String(b.item_name || '')))
      .slice(0, 6)
  ), [inventoryItems]);

  // State for leave form
  const [leaveForm, setLeaveForm] = useState({
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: "",
    attachment: null // file for sick leave (image/pdf)
  });
  const [leaveDateError, setLeaveDateError] = useState("");
  const [, setSubmitMessage] = useState("");
  const [, setSubmitError] = useState("");

  const getManilaTodayISO = () => {
    try {
      const fmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Manila',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return fmt.format(new Date());
    } catch (e) {
      return getCurrentDateKey();
    }
  };
  const formatDateTime = (value) => value ? `${formatDateOnly(value)} ${formatDateTimeToTime12h(value)}`.trim() : '';
  const toIsoDate = (value) => {
    return toDateKey(value);
  };
  const buildAttendanceDateTime = React.useCallback((dateValue, timeValue) => {
    const dateKey = toDateKey(dateValue || new Date());
    const timePart = String(timeValue || '').trim();
    if (!dateKey || !timePart) return null;
    const parsed = new Date(`${dateKey}T${timePart}+08:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);
  const formatCountdownLabel = (remainingMs) => {
    const clamped = Math.max(0, Number(remainingMs || 0));
    const totalSeconds = Math.floor(clamped / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  const buildCutoffLabel = (startDate, endDate) => {
    const startLabel = formatDateOnly(startDate);
    const endLabel = formatDateOnly(endDate);
    if (startLabel && endLabel && startLabel !== endLabel) return `${startLabel}–${endLabel}`;
    return startLabel || endLabel || '';
  };
  const buildPayslipFromHistoryRow = React.useCallback((row) => {
    if (row && row.employee && row.period && row.earnings && row.deductions && row.totals) {
      return row;
    }
    const month = Number(row?.month || 0) || null;
    const year = Number(row?.year || 0) || null;
    const periodStart = (month && year) ? new Date(year, month - 1, 1) : null;
    const periodEnd = (month && year) ? new Date(year, month, 0) : null;
    const basicPay = Number(row?.base_salary || 0);
    const netPay = Number(row?.net_salary || 0);
    const absences = Number(row?.absences || 0);
    const grossPay = Number.isFinite(basicPay) ? basicPay : 0;
    const totalDeductions = Math.max(0, grossPay - (Number.isFinite(netPay) ? netPay : 0));
    const fullName = fullDisplayName || user?.username || '';
    const daysInMonth = periodEnd ? periodEnd.getDate() : 0;
    const daysWorked = Math.max(0, daysInMonth - Math.max(0, absences));
    const dailyRate = daysWorked > 0 ? (basicPay / daysWorked) : Number(user?.baseSalary || user?.base_salary || user?.base_salary_per_day || 0);

    return {
      employee: {
        full_name: fullName,
        employee_no: user?.employeeid || user?.employee_no || user?.username || '',
        position_type: normalizedRole ? normalizedRole.charAt(0).toUpperCase() + normalizedRole.slice(1) : ''
      },
      period: {
        start: toIsoDate(periodStart),
        end: toIsoDate(periodEnd),
        cutoff_label: buildCutoffLabel(periodStart, periodEnd)
      },
      earnings: {
        basic_pay: basicPay,
        days_worked: daysWorked,
        daily_rate: Number.isFinite(dailyRate) ? dailyRate : 0,
        overtime_pay: 0,
        holiday_pay: 0,
        allowances: 0,
        gross_pay: grossPay
      },
      deductions: {
        sss: 0,
        philhealth: 0,
        pagibig: 0,
        tax: 0,
        late: 0,
        absences: {
          days: absences,
          amount: 0
        },
        others: 0,
        total_deductions: totalDeductions
      },
      totals: {
        net_pay: Number.isFinite(netPay) ? netPay : grossPay - totalDeductions
      }
    };
  }, [normalizedRole, user]);

  const parseIsoDate = (iso) => {
    if (!iso) return null;
    const parts = String(iso).split('-').map(Number);
    if (parts.length !== 3) return null;
    const [y, m, d] = parts;
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    return new Date(y, m - 1, d);
  };

  const formatIsoDate = (d) => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const formatDateOnly = (dateString) => {
    return formatManilaDateOnly(dateString) || dateString;
  };

  const isWeekendDate = (d) => {
    if (!d) return false;
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  const nextWorkingDayISO = (iso) => {
    const d0 = parseIsoDate(iso);
    if (!d0) return null;
    const d = new Date(d0.getTime());
    do {
      d.setDate(d.getDate() + 1);
    } while (isWeekendDate(d));
    return formatIsoDate(d);
  };

  const countWorkingDaysInclusive = (startIso, endIso) => {
    const s = parseIsoDate(startIso);
    const e = parseIsoDate(endIso);
    if (!s || !e) return 0;
    let count = 0;
    const d = new Date(s.getTime());
    while (d.getTime() <= e.getTime()) {
      if (!isWeekendDate(d)) count += 1;
      d.setDate(d.getDate() + 1);
    }
    return count;
  };

  const rangeHasWeekend = (startIso, endIso) => {
    const s = parseIsoDate(startIso);
    const e = parseIsoDate(endIso);
    if (!s || !e) return false;
    const d = new Date(s.getTime());
    while (d.getTime() <= e.getTime()) {
      if (isWeekendDate(d)) return true;
      d.setDate(d.getDate() + 1);
    }
    return false;
  };

  const manilaTodayISO = React.useMemo(() => getManilaTodayISO(), []);
  const manilaTodayDate = React.useMemo(() => parseIsoDate(manilaTodayISO), [manilaTodayISO]);
  const manilaNextWorkingISO = React.useMemo(() => nextWorkingDayISO(manilaTodayISO), [manilaTodayISO]);

  // State for payroll
  const [, setPayrollData] = useState([]);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [, setPayslipOpen] = useState(false);
  const [, setPayslipPayroll] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [personalPayrollCutoffs, setPersonalPayrollCutoffs] = useState([]);
  const [selectedPayslipCutoffId, setSelectedPayslipCutoffId] = useState('');
  const [payrollPreview, setPayrollPreview] = useState(null);
  const [alreadyTimedInBanner, setAlreadyTimedInBanner] = useState(false);
  // Employee attendance (non-camera) dialogs/state
  const [timeInDialogOpen, setTimeInDialogOpen] = useState(false);
  const [timeOutDialogOpen, setTimeOutDialogOpen] = useState(false);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceMessage, setAttendanceMessage] = useState('');
  const [attendanceError, setAttendanceError] = useState('');
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [selfAttendanceFilter, setSelfAttendanceFilter] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [hasOpenShift, setHasOpenShift] = useState(false);
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [openShiftRecord, setOpenShiftRecord] = useState(null);
  const [breakCountdownMs, setBreakCountdownMs] = useState(0);

  const employmentStart = React.useMemo(() => {
    const raw = user?.date_hired || user?.dateHired || null;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }, [user?.date_hired, user?.dateHired]);

  const nowRef = new Date();
  const currentMonth = nowRef.getMonth() + 1;
  const currentYear = nowRef.getFullYear();
  const minYear = employmentStart ? employmentStart.getFullYear() : (currentYear - 3);
  const minMonth = employmentStart ? (employmentStart.getMonth() + 1) : 1;

  const getMonthBoundsForYear = React.useCallback((year) => {
    let start = 1;
    let end = 12;
    if (year === minYear) start = minMonth;
    if (year === currentYear) end = currentMonth;
    if (start > end) start = end;
    return { start, end };
  }, [minYear, minMonth, currentYear, currentMonth]);

  const availableYears = React.useMemo(() => {
    const years = [];
    for (let y = minYear; y <= currentYear; y += 1) years.push(y);
    return years.length > 0 ? years : [currentYear];
  }, [minYear, currentYear]);

  useEffect(() => {
    const y = Number(selectedYear);
    const m = Number(selectedMonth);
    const clampedYear = Math.min(Math.max(y, minYear), currentYear);
    const bounds = getMonthBoundsForYear(clampedYear);
    const clampedMonth = Math.min(Math.max(m, bounds.start), bounds.end);
    if (clampedYear !== y) setSelectedYear(clampedYear);
    if (clampedMonth !== m) setSelectedMonth(clampedMonth);
  }, [selectedYear, selectedMonth, minYear, currentYear, getMonthBoundsForYear]);

  // Fetch leave requests when "file-leave" or "see-leave-requests" section is active
  // --- Stable fetch helpers (hoisted here so effects can reference them) ---
  const fetchLeaveRequests = React.useCallback(async () => {
    setLeaveLoading(true);
    setLeaveError(null);
    try {
      const response = await axios.get(`${API_BASE}/api/leave-requests/me`, {
        withCredentials: true
      });
      const rows = Array.isArray(response?.data?.rows)
        ? response.data.rows
        : (Array.isArray(response?.data) ? response.data : []);
      setLeaveRequests(rows);
    } catch (error) {
      setLeaveError("Failed to load leave requests");
      setLeaveRequests([]);
      console.error(error);
    } finally {
      setLeaveLoading(false);
    }
  }, []);

  const fetchUnreadInboxCount = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/notifications`, { withCredentials: true });
      const rows = Array.isArray(res?.data) ? res.data : [];
      setUnreadInboxCount(rows.filter((n) => !n.is_read).length);
    } catch (e) {
      // non-blocking badge refresh only
    }
  }, []);

  const fetchInventoryRequests = React.useCallback(async () => {
    setInventoryLoading(true);
    setInventoryError(null);
    try {
      const response = await axios.get(`${API_BASE}/api/inventory/requests`, {
        withCredentials: true
      });
      setInventoryRequests(response.data);
    } catch (error) {
      setInventoryError("Failed to load inventory requests");
      console.error(error);
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  const fetchInventoryItems = React.useCallback(async () => {
    setInventoryError(null);
    try {
      // Prefer canonical items-with-stock endpoint
      try {
        const res = await axios.get(`${API_BASE}/api/inventory/items-with-stock`, { withCredentials: true });
        setInventoryItems(Array.isArray(res.data) ? res.data : []);
      } catch (inner) {
        const res2 = await axios.get(`${API_BASE}/api/inventory-items`, { withCredentials: true });
        const raw = Array.isArray(res2.data) ? res2.data : [];
        const normalized = raw.map((i) => (typeof i === 'string' ? { id: null, item_name: i, quantity: 0 } : i));
        setInventoryItems(normalized);
      }
    } catch (err) {
      console.error('Failed to load inventory items:', err);
    }
  }, []);

  const fetchDtrPhotos = React.useCallback(async () => {
    setDtrPhotosLoading(true);
    setDtrPhotosError('');
    try {
      const res = await axios.get(`${API_BASE}/api/attendance/dtr-evidence`, { withCredentials: true });
      const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setDtrPhotos(rows);
    } catch (err) {
      console.error('Failed to load DTR photos:', err);
      setDtrPhotosError(err?.response?.data?.error || 'Failed to load DTR photos.');
      setDtrPhotos([]);
    } finally {
      setDtrPhotosLoading(false);
    }
  }, []);

  const getDtrReviewLabel = (status) => {
    const s = String(status || '').trim().toLowerCase();
    if (s === 'validated') return 'Validated by Employee';
    if (s === 'needs_edit') return 'Needs Attendance Edit';
    if (s === 'rejected') return 'Rejected by Employee';
    return 'Pending Employee Review';
  };

  const renderDtrReviewChip = (status) => {
    const s = String(status || '').trim().toLowerCase();
    const color = s === 'validated' ? 'success' : (s === 'needs_edit' ? 'warning' : (s === 'rejected' ? 'error' : 'default'));
    return <Chip size="small" color={color} label={getDtrReviewLabel(status)} />;
  };

  const getDtrReviewerLabel = (row) => {
    const status = String(row?.employee_review_status || '').trim().toLowerCase();
    if (!status) return '';
    const reviewerId = String(row?.employee_reviewer_employeeid || row?.employee_reviewed_by || '').trim();
    const reviewerName = String(row?.employee_reviewer_name || '').trim();
    if (!reviewerId && !reviewerName) return '';
    const actionLabel = status === 'validated' ? 'Approved by' : 'Reviewed by';
    return `${actionLabel} ${reviewerId ? String(reviewerId).padStart(8, '0') : ''}${reviewerName ? ` - ${reviewerName}` : ''}`.trim();
  };

  const getDtrMonthYear = (row) => {
    const raw = row?.captured_at || row?.created_at || new Date();
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return { month: selectedMonth, year: selectedYear };
    }
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  };

  const getMonthName = (month) => {
    const idx = Number(month || 0) - 1;
    if (idx < 0 || idx > 11) return '';
    return new Date(2026, idx, 1).toLocaleString('en-US', { month: 'long' });
  };

  const fetchCompanyAttendance = React.useCallback(async ({ month = selectedMonth, year = selectedYear, employeeId = '' } = {}) => {
    setCompanyAttendanceLoading(true);
    setCompanyAttendanceError('');
    try {
      const params = new URLSearchParams({
        scope: 'company',
        month: String(month),
        year: String(year),
      });
      if (employeeId) params.set('employee_id', String(employeeId));
      const res = await axios.get(`${API_BASE}/api/attendance?${params.toString()}`, { withCredentials: true });
      const rows = Array.isArray(res?.data) ? res.data : (Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setCompanyAttendanceRows(rows);
      return rows;
    } catch (err) {
      console.error('Company attendance fetch error:', err);
      const msg = err?.response?.data?.error || 'Failed to load company attendance.';
      setCompanyAttendanceError(msg);
      setCompanyAttendanceRows([]);
      return [];
    } finally {
      setCompanyAttendanceLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  const visibleCompanyAttendanceRows = React.useMemo(() => {
    const roleFilter = String(companyAttendanceFilters.role || 'all').toLowerCase();
    const statusFilter = String(companyAttendanceFilters.status || 'all').toLowerCase();
    const methodFilter = String(companyAttendanceFilters.method || 'all').toLowerCase();
    const employeeIdFilter = String(companyAttendanceFilters.employeeId || '').replace(/\D/g, '');
    return (companyAttendanceRows || []).filter((row) => {
      const role = String(row?.role || '').toLowerCase();
      if (roleFilter !== 'all' && role !== roleFilter) return false;
      const methodLabel = String(getAttendanceMethodLabel(row) || '').toLowerCase();
      if (methodFilter !== 'all') {
        if (methodFilter === 'face' && methodLabel !== 'face') return false;
        if (methodFilter === 'manual' && !methodLabel.includes('manual')) return false;
        if (methodFilter === 'leave' && !methodLabel.includes('leave')) return false;
      }
      if (employeeIdFilter) {
        const rowEmployeeId = String(row?.employeeid || row?.employee_no || row?.username || '').replace(/\D/g, '');
        if (!rowEmployeeId.includes(employeeIdFilter)) return false;
      }
      const statusLabel = String(getAttendanceStatusLabel(row) || '').toLowerCase();
      if (statusFilter === 'present' && !statusLabel.includes('present')) return false;
      if (statusFilter === 'absent' && !statusLabel.includes('absent')) return false;
      if (statusFilter === 'half_day' && !statusLabel.includes('half day')) return false;
      if (statusFilter === 'leave' && !statusLabel.includes('leave')) return false;
      return true;
    });
  }, [companyAttendanceRows, companyAttendanceFilters.role, companyAttendanceFilters.status, companyAttendanceFilters.method, companyAttendanceFilters.employeeId]);

  const openDtrCompare = async (row) => {
    const employeeId = row?.employeeid || row?.username || '';
    const { month, year } = getDtrMonthYear(row);
    setDtrCompareDialog({ open: true, row, rows: [], loading: true, error: '', month, year });
    try {
      const rows = await fetchCompanyAttendance({ month, year, employeeId });
      setDtrCompareDialog({ open: true, row, rows, loading: false, error: '', month, year });
    } catch (err) {
      setDtrCompareDialog({ open: true, row, rows: [], loading: false, error: 'Failed to load matching attendance.', month, year });
    }
  };

  const shiftDtrCompareMonth = async (offset) => {
    const row = dtrCompareDialog.row;
    if (!row) return;
    const currentMonthValue = Number(dtrCompareDialog.month || getDtrMonthYear(row).month);
    const currentYearValue = Number(dtrCompareDialog.year || getDtrMonthYear(row).year);
    const nextDate = new Date(currentYearValue, currentMonthValue - 1 + offset, 1);
    const month = nextDate.getMonth() + 1;
    const year = nextDate.getFullYear();
    const employeeId = row?.employeeid || row?.username || '';
    setDtrCompareDialog((prev) => ({ ...prev, rows: [], loading: true, error: '', month, year }));
    try {
      const rows = await fetchCompanyAttendance({ month, year, employeeId });
      setDtrCompareDialog((prev) => ({ ...prev, rows, loading: false, error: '', month, year }));
    } catch (err) {
      setDtrCompareDialog((prev) => ({ ...prev, rows: [], loading: false, error: 'Failed to load matching attendance.', month, year }));
    }
  };

  const openDtrReview = (row, status) => {
    setDtrReviewDialog({ open: true, row, status, notes: '', saving: false, error: '' });
  };

  const visibleSelfAttendanceRows = React.useMemo(() => {
    const month = Number(selfAttendanceFilter.month);
    const year = Number(selfAttendanceFilter.year);
    return (attendanceRows || []).filter((row) => {
      const key = toDateKey(row.log_date || row.date);
      if (!key) return false;
      const d = new Date(`${key}T00:00:00`);
      return d.getMonth() + 1 === month && d.getFullYear() === year;
    });
  }, [attendanceRows, selfAttendanceFilter.month, selfAttendanceFilter.year]);

  const submitDtrReview = async () => {
    const row = dtrReviewDialog.row;
    const status = dtrReviewDialog.status;
    if (!row?.id || !status) return;
    setDtrReviewDialog((prev) => ({ ...prev, saving: true, error: '' }));
    try {
      const res = await axios.post(
        `${API_BASE}/api/attendance/dtr-evidence/${row.id}/employee-review`,
        { status, notes: dtrReviewDialog.notes },
        { withCredentials: true }
      );
      const updated = res?.data?.evidence;
      if (updated) {
        setDtrPhotos((rows) => rows.map((item) => String(item.id) === String(updated.id) ? { ...item, ...updated } : item));
      } else {
        await fetchDtrPhotos();
      }
      setDtrReviewDialog({ open: false, row: null, status: '', notes: '', saving: false, error: '' });
      try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
    } catch (err) {
      setDtrReviewDialog((prev) => ({
        ...prev,
        saving: false,
        error: err?.response?.data?.error || 'Failed to save DTR review.',
      }));
    }
  };

  // Stable payroll fetch (useCallback so it can be used safely in effects)
  const fetchPayrollData = React.useCallback(async (month = selectedMonth, year = selectedYear) => {
    if (normalizedRole !== 'employee') return;
    setPayrollLoading(true);
    setPayrollError(null);
    try {
      const row = await fetchPayslipRow(API_BASE, { month, year, cutoffId: selectedPayslipCutoffId });
      if (!row) {
        setPayslip(null);
        setPayrollError(null);
      } else {
        setPayslip(buildPayslipFromHistoryRow(row));
        setPayrollError(null);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        setPayrollError('Session expired. Please login again.');
      } else if (error?.response?.status === 404) {
        setPayrollError(null);
      } else {
        setPayrollError('Failed to load payslip.');
        console.error('Fetch payslip error:', error);
      }
      setPayslip(null);
    } finally {
      setPayrollLoading(false);
    }
  }, [buildPayslipFromHistoryRow, normalizedRole, selectedMonth, selectedYear, selectedPayslipCutoffId]);

  const fetchPersonalPayrollCutoffs = React.useCallback(async () => {
    if (normalizedRole !== 'employee') return;
    try {
      const res = await axios.get(`${API_BASE}/api/payroll/cutoffs/me`, { withCredentials: true });
      const rows = Array.isArray(res.data) ? res.data : [];
      setPersonalPayrollCutoffs(rows);
      setSelectedPayslipCutoffId((prev) => prev || (rows[0]?.id ? String(rows[0].id) : ''));
    } catch (error) {
      console.error('Failed to load personal payroll cutoffs:', error);
      setPersonalPayrollCutoffs([]);
    }
  }, [normalizedRole]);

  const handlePayslipPrev = () => {
    if (selectedYear === minYear && selectedMonth <= minMonth) return;
    setSelectedMonth((prevMonth) => {
      if (prevMonth === 1) {
        setSelectedYear((y) => y - 1);
        return 12;
      }
      return prevMonth - 1;
    });
  };

  const handlePayslipNext = () => {
    if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonth >= currentMonth)) return;
    setSelectedMonth((prevMonth) => {
      if (prevMonth === 12) {
        setSelectedYear((y) => y + 1);
        return 1;
      }
      return prevMonth + 1;
    });
  };

  const currentPayslip = payslip;
  const printPayslipOnly = () => {
    const node = document.querySelector('.payslip-print');
    if (!node) return window.print();
    const popup = window.open('', '_blank', 'width=900,height=1200');
    if (!popup) return window.print();
    popup.document.write(`
      <html>
        <head>
          <title>Payslip</title>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #111; }
            * { box-sizing: border-box; }
            table { width: 100% !important; border-collapse: collapse; table-layout: fixed; }
            th, td { border: 1px solid #d0d0d0; padding: 6px; vertical-align: top; word-break: break-word; }
          </style>
        </head>
        <body>${node.outerHTML}</body>
      </html>
    `);
    popup.document.close();
    popup.focus();
    popup.print();
    popup.close();
  };

  useEffect(() => {
    if (!hasLeaveHistoryIdentity || mustChangePassword) return;
    if (activeSection === 'file-leave' || activeSection === 'see-leave-requests') {
      fetchLeaveRequests();
    }
  }, [activeSection, fetchLeaveRequests, hasLeaveHistoryIdentity, mustChangePassword]);



  // Fetch notifications when Inbox is active
  useEffect(() => {
    if (mustChangePassword) return;
    if (activeSection === 'inbox') fetchUnreadInboxCount();
  }, [activeSection, fetchUnreadInboxCount]);

  // Initial badge data load
  useEffect(() => {
    if (mustChangePassword) return;
    fetchUnreadInboxCount();
    fetchInventoryRequests();
    fetchInventoryItems();
  }, [mustChangePassword, fetchUnreadInboxCount, fetchInventoryRequests, fetchInventoryItems]);

  // Listen for external refresh requests (e.g., after HR approves a leave)
  useEffect(() => {
    if (mustChangePassword) return;
    const handler = () => { try { fetchUnreadInboxCount(); } catch (e) {} };
    window.addEventListener('refreshNotifications', handler);
    return () => window.removeEventListener('refreshNotifications', handler);
  }, [fetchUnreadInboxCount]);

  // Poll for sidebar badge counts.
  useEffect(() => {
    if (mustChangePassword) return;
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      try { fetchUnreadInboxCount(); } catch (e) {}
      try { fetchInventoryRequests(); } catch (e) {}
    }, 30000);
    return () => { clearInterval(iv); };
  }, [fetchUnreadInboxCount, fetchInventoryRequests]);

  // Fetch inventory requests/items when inventory or inventory-request section is active
  useEffect(() => {
    if (mustChangePassword) return;
    if (activeSection === 'inventory-request' || activeSection === 'inventory') {
      fetchInventoryRequests();
      fetchInventoryItems();
    }
  }, [activeSection, fetchInventoryRequests, fetchInventoryItems]);

  useEffect(() => {
    if (mustChangePassword) return;
    if (activeSection === 'dtr-photos') {
      fetchDtrPhotos();
    }
  }, [activeSection, fetchDtrPhotos, mustChangePassword]);

  useEffect(() => {
    if (mustChangePassword) return;
    if (activeSection === 'attendance-record') {
      fetchCompanyAttendance({ month: companyAttendanceFilters.month, year: companyAttendanceFilters.year });
    }
  }, [activeSection, fetchCompanyAttendance, mustChangePassword, companyAttendanceFilters.month, companyAttendanceFilters.year]);

  // Fetch payroll data when "payroll" section is active
  useEffect(() => {
    if (mustChangePassword) return;
    if (normalizedRole !== 'employee') return;
    if (activeSection === "payroll") {
      fetchPersonalPayrollCutoffs();
      fetchPayrollData(selectedMonth, selectedYear);
    }
  }, [activeSection, fetchPayrollData, fetchPersonalPayrollCutoffs, normalizedRole, selectedMonth, selectedYear, selectedPayslipCutoffId]);

  useEffect(() => {
    fetchRecentAttendance();
  }, [user?.username]);

  // Poll for payroll disburse events so custodians/employees see updates in near-real-time
  useEffect(() => {
    if (mustChangePassword) return;
    if (normalizedRole !== 'employee') return;
    let interval = null;
    let lastSeen = null;
    const poll = async () => {
      try {
  const res = await axios.get(`${API_BASE}/api/payroll/last-disburse`);
        const last = res.data?.lastDisburseAt || null;
        if (last && last !== lastSeen) {
          lastSeen = last;
          // refresh payroll data when a new disburse/update occurred
          if (activeSection === 'payroll') fetchPayrollData();
        }
      } catch (err) {
        // ignore polling errors
      }
    };

    if (activeSection === 'payroll') {
      // initial fetch is handled above; poll every 30 seconds while visible
      interval = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        poll();
      }, 30000);
      // run immediately once
      poll();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeSection, fetchPayrollData, normalizedRole]);

  // Inventory request dialog state
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestItem, setRequestItem] = useState(null);
  const [requestQuantity, setRequestQuantity] = useState(1);
  const [requestNote, setRequestNote] = useState('');
  const [requestError, setRequestError] = useState('');

  const handleApproveInventory = async (id) => {
    try {
      await axios.post(`${API_BASE}/api/inventory/requests/${id}/approve`, {}, { withCredentials: true });
      await fetchInventoryRequests(); // Refresh the list
      await fetchInventoryItems();
      // notify notification panel to refresh
      try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
    } catch (error) {
      console.error("Failed to approve inventory request:", error);
      setInventoryError(error?.response?.data?.error || 'Failed to approve inventory request');
    }
  };

  const handleRejectInventory = async (id) => {
    try {
      await axios.post(`${API_BASE}/api/inventory/requests/${id}/reject`, {}, { withCredentials: true });
      fetchInventoryRequests(); // Refresh the list
    } catch (error) {
      console.error("Failed to reject inventory request:", error);
      setInventoryError(error?.response?.data?.error || 'Failed to reject inventory request');
    }
  };

  const getAllowedStartDates = (type) => {
    if (type !== 'sick' && type !== 'calamity') return [];
    const dates = [];
    if (manilaTodayDate && !isWeekendDate(manilaTodayDate)) dates.push(manilaTodayISO);
    if (manilaNextWorkingISO && !dates.includes(manilaNextWorkingISO)) dates.push(manilaNextWorkingISO);
    return dates;
  };

  const getMaxWorkingDaysForType = (type) => {
    if (type === 'sick' || type === 'calamity') {
      const info = allowedTypesByKey[type] || {};
      return Number(info.maxDays || (type === 'sick' ? 4 : 5));
    }
    return null;
  };

  const getMaxCalendarDaysForType = (type) => {
    const info = allowedTypesByKey[type] || {};
    const max = info.maxDays;
    return Number.isFinite(Number(max)) ? Number(max) : null;
  };

  const validateRange = (type, startIso, endIso) => {
    if (!startIso || !endIso) return '';
    const s = parseIsoDate(startIso);
    const e = parseIsoDate(endIso);
    if (!s || !e) return 'Invalid date';
    if (e.getTime() < s.getTime()) return 'End date must be on or after start date.';
    if (isWeekendDate(s) || isWeekendDate(e)) return 'Weekends are not allowed for leave dates.';
    if (rangeHasWeekend(startIso, endIso)) return 'Leave range cannot include weekends.';

    if (type === 'sick' || type === 'calamity') {
      const allowedStarts = getAllowedStartDates(type);
      if (!allowedStarts.includes(startIso)) {
        return 'Start date must be today or the next working day.';
      }
      const maxWd = getMaxWorkingDaysForType(type);
      const wd = countWorkingDaysInclusive(startIso, endIso);
      if (maxWd && wd > maxWd) {
        return `Maximum allowed working days for ${type === 'sick' ? 'Sick Leave' : 'Calamity Leave'} is ${maxWd}.`;
      }
      return '';
    }

    const maxCal = getMaxCalendarDaysForType(type);
    if (maxCal) {
      const diffDays = Math.floor((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1;
      if (diffDays > maxCal) return `Maximum allowed days is ${maxCal}.`;
    }
    return '';
  };

  const handleLeaveTypeChange = (e) => {
    const type = e.target.value;
    if (type !== 'sick' && type !== 'calamity') {
      setLeaveDateError('');
      setLeaveForm({ ...leaveForm, leave_type: type, start_date: '', end_date: '' });
      return;
    }
    const allowedStarts = getAllowedStartDates(type);
    let startIso = allowedStarts.length > 0 ? allowedStarts[0] : '';
    let endIso = startIso;
    if (startIso && endIso) {
      const err = validateRange(type, startIso, endIso);
      if (err) {
        setLeaveDateError(err);
      } else {
        setLeaveDateError('');
      }
    } else {
      setLeaveDateError('');
    }
    setLeaveForm({ ...leaveForm, leave_type: type, start_date: startIso, end_date: endIso });
  };

  const shouldDisableStartDate = (date) => {
    if (!date) return false;
    if (isWeekendDate(date)) return true;
    const type = leaveForm.leave_type;
    if (type === 'sick' || type === 'calamity') {
      const iso = formatIsoDate(date);
      const allowed = getAllowedStartDates(type);
      return !allowed.includes(iso);
    }
    return false;
  };

  const shouldDisableEndDate = (date) => {
    if (!date) return false;
    if (isWeekendDate(date)) return true;
    const type = leaveForm.leave_type;
    const startIso = leaveForm.start_date;
    if (!startIso) return false;
    const endIso = formatIsoDate(date);
    const s = parseIsoDate(startIso);
    const e = parseIsoDate(endIso);
    if (!s || !e) return false;
    if (e.getTime() < s.getTime()) return true;
    if (rangeHasWeekend(startIso, endIso)) return true;
    if (type === 'sick' || type === 'calamity') {
      const maxWd = getMaxWorkingDaysForType(type);
      const wd = countWorkingDaysInclusive(startIso, endIso);
      if (maxWd && wd > maxWd) return true;
    } else {
      const maxCal = getMaxCalendarDaysForType(type);
      if (maxCal) {
        const diff = Math.floor((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000)) + 1;
        if (diff > maxCal) return true;
      }
    }
    return false;
  };

  const handleStartDateChange = (newVal) => {
    if (!newVal) {
      setLeaveForm({ ...leaveForm, start_date: '' });
      setLeaveDateError('');
      return;
    }
    const iso = formatIsoDate(new Date(newVal));
    const type = leaveForm.leave_type;
    const allowedStarts = getAllowedStartDates(type);
    if ((type === 'sick' || type === 'calamity') && !allowedStarts.includes(iso)) {
      setLeaveDateError('Start date must be today or the next working day.');
      return;
    }
    let endIso = leaveForm.end_date;
    if (endIso) {
      const s = parseIsoDate(iso);
      const en = parseIsoDate(endIso);
      if (s && en && en.getTime() < s.getTime()) endIso = iso;
      const err = validateRange(type, iso, endIso);
      if (err) {
        endIso = iso;
        setLeaveDateError(err);
      } else {
        setLeaveDateError('');
      }
    } else {
      setLeaveDateError('');
    }
    setLeaveForm({ ...leaveForm, start_date: iso, end_date: endIso || '' });
  };

  const handleEndDateChange = (newVal) => {
    if (!newVal) {
      setLeaveForm({ ...leaveForm, end_date: '' });
      setLeaveDateError('');
      return;
    }
    const iso = formatIsoDate(new Date(newVal));
    const type = leaveForm.leave_type;
    let startIso = leaveForm.start_date || iso;
    const err = validateRange(type, startIso, iso);
    if (err) {
      setLeaveDateError(err);
      return;
    }
    setLeaveDateError('');
    setLeaveForm({ ...leaveForm, start_date: startIso, end_date: iso });
  };

  const handleLeaveFormChange = (e) => {
    if (e.target.type === 'file') {
      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
      setLeaveForm({ ...leaveForm, attachment: file });
      return;
    }
    if (e.target.name === 'leave_type') {
      handleLeaveTypeChange(e);
      return;
    }
    setLeaveForm({ ...leaveForm, [e.target.name]: e.target.value });
  };

  const handleSubmitLeave = async () => {
    if (!leaveForm.leave_type || !leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason) {
      setSubmitError("Please fill in all fields.");
      return;
    }
    const rangeErr = validateRange(leaveForm.leave_type, leaveForm.start_date, leaveForm.end_date);
    if (leaveDateError || rangeErr) {
      setSubmitError(leaveDateError || rangeErr);
      return;
    }
    try {
      // If attachment present, send as multipart/form-data
      if (leaveForm.attachment) {
        const fd = new FormData();
        fd.append('leave_type', leaveForm.leave_type);
        fd.append('start_date', leaveForm.start_date);
        fd.append('end_date', leaveForm.end_date);
        fd.append('reason', leaveForm.reason);
        fd.append('attachment', leaveForm.attachment);
        await axios.post(`${API_BASE}/api/leave-requests`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        });
      } else {
        await axios.post(`${API_BASE}/api/leave-requests`, leaveForm, {
          withCredentials: true
        });
      }
      setSubmitMessage("Leave request submitted successfully!");
      setLeaveForm({
        leave_type: "",
        start_date: "",
        end_date: "",
        reason: "",
        attachment: null
      });
      setSubmitError("");
      fetchLeaveRequests(); // Refresh the list
      try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
    } catch (error) {
      const serverMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      setSubmitError(serverMsg || "Failed to submit leave request. Please try again.");
      console.error('submit leave error', error, 'serverMsg=', serverMsg);
    }
  };

  const fetchRecentAttendance = async () => {
    try {
      setAttendanceLoading(true);
      const res = await axios.get(`${API_BASE}/api/attendance`, { withCredentials: true });
      const payload = res.data;
      const rows = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.rows) ? payload.rows : []);
      setAttendanceRows(rows);
      setRecentAttendance(rows);
      const todayStr = getCurrentDateKey();
      const normalizeDate = (v) => {
        return toDateKey(v);
      };
      const openShift = rows.find((row) => {
        const rowDate = normalizeDate(row.log_date || row.date);
        const manualPending = String(row?.manual_request_status || '').toLowerCase() === 'pending';
        const hasTimeIn = Boolean(row?.log_time || row?.time_in || row?.timeIn);
        const hasTimeOut = Boolean(row?.time_out || row?.timeOut);
        const isAbsent = Number(row?.is_absent || 0) === 1;
        const isLeave = Number(row?.leave_approved || 0) === 1 || String(row?.status || '').toLowerCase() === 'leave';
        return rowDate === todayStr && !manualPending && hasTimeIn && !hasTimeOut && !isAbsent && !isLeave;
      });
      setHasOpenShift(!!openShift);
      setIsOnBreak(Boolean(openShift && openShift.break_start && !openShift.break_end));
      setOpenShiftRecord(openShift || null);
    } catch (err) {
      console.error('Attendance user fetch error:', err);
    } finally {
      setAttendanceLoading(false);
    }
  };

  useEffect(() => {
    const activeBreakStart = openShiftRecord?.break_start;
    if (!isOnBreak || !activeBreakStart) {
      setBreakCountdownMs(0);
      return undefined;
    }

    const breakStartedAt = buildAttendanceDateTime(openShiftRecord?.log_date || new Date(), activeBreakStart);
    if (!breakStartedAt) {
      setBreakCountdownMs(0);
      return undefined;
    }

    const breakEndsAt = new Date(breakStartedAt.getTime() + (60 * 60 * 1000));
    const updateCountdown = () => {
      setBreakCountdownMs(Math.max(0, breakEndsAt.getTime() - Date.now()));
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [buildAttendanceDateTime, isOnBreak, openShiftRecord]);

  const activeBreakStartAt = React.useMemo(() => {
    if (!isOnBreak || !openShiftRecord?.break_start) return null;
    return buildAttendanceDateTime(openShiftRecord?.log_date || new Date(), openShiftRecord.break_start);
  }, [buildAttendanceDateTime, isOnBreak, openShiftRecord]);

  const activeBreakEndAt = React.useMemo(() => {
    if (!activeBreakStartAt) return null;
    return new Date(activeBreakStartAt.getTime() + (60 * 60 * 1000));
  }, [activeBreakStartAt]);

  const breakStartLabel = activeBreakStartAt ? formatDateTimeToTime12h(activeBreakStartAt) : '-';
  const breakReturnLabel = activeBreakEndAt ? formatDateTimeToTime12h(activeBreakEndAt) : '-';
  const breakCountdownLabel = formatCountdownLabel(breakCountdownMs);

  // Employee attendance API calls (no camera)
  const markTimeInEmployee = async ({ log_date, log_time } = {}) => {
    setAttendanceLoading(true);
    setAttendanceError('');
    setAttendanceMessage('');
    try {
      const res = await axios.post(`${API_BASE}/api/attendance/employee/mark-time-in`, { log_date, log_time }, { withCredentials: true });
      const payload = res?.data || {};
      const autoTimedOut = payload?.autoTimedOut || null;
      const ok = payload?.ok === true || typeof payload?.ok === 'undefined';
      const canProceed = handleTimeInResponse(payload);
      if (!ok || !canProceed) {
        return;
      }
      // show lateness / deduction estimate if provided
      if (payload) {
        const mins = payload.lateMinutes || 0;
        const ded = payload.deductionEstimate || 0;
        if (mins > 0) {
          let msg = `${payload.message || 'Time-in recorded'} — Late by ${mins} minute(s). Estimated deduction: ₱${ded.toFixed ? ded.toFixed(2) : Number(ded).toFixed(2)}`;
          if (autoTimedOut) msg += ' Previous shift was auto time-out after 8 hours.';
          setAttendanceMessage(msg);
        } else {
          let msg = payload.message || 'Time-in recorded';
          if (autoTimedOut) msg += ' Previous shift was auto time-out after 8 hours.';
          setAttendanceMessage(msg);
        }
      } else {
        setAttendanceMessage('Time-in recorded');
      }
  try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
  // notify other parts of the app (HR payroll) that attendance changed and payroll may need refresh
  try { window.dispatchEvent(new Event('payrollUpdated')); } catch (e) {}
      fetchRecentAttendance();
    } catch (err) {
      console.error('markTimeInEmployee error', err);
      setAttendanceError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Failed to record time-in');
    } finally {
      setAttendanceLoading(false);
      setTimeInDialogOpen(false);
    }
  };

  const handleTimeInResponse = (payload) => {
    if (!payload) {
      setAlreadyTimedInBanner(false);
      setAttendanceError('Failed to record time in. Please try again.');
      return false;
    }
    if (payload.ok === false && payload.code === 'OPEN_SHIFT_EXISTS') {
      setAlreadyTimedInBanner(true);
      setHasOpenShift(true);
      setAttendanceError(payload.message || "You already timed in and haven't timed out yet.");
      return false;
    }
    if (payload.ok !== true && typeof payload.ok !== 'undefined') {
      setAlreadyTimedInBanner(false);
      setAttendanceError(payload.message || 'Failed to record time in. Please try again.');
      return false;
    }
    setAlreadyTimedInBanner(false);
    setHasOpenShift(true);
    setIsOnBreak(false);
    if (payload.payrollPreview) {
      setPayrollPreview(payload.payrollPreview);
    }
    return true;
  };

  const markTimeOutEmployee = async ({ log_date, log_time } = {}) => {
    if (!hasOpenShift) {
      setAttendanceError('You have not timed in today. Time out is disabled.');
      return;
    }
    if (isOnBreak) {
      setAttendanceError('End break before timing out.');
      return;
    }
    setAttendanceLoading(true);
    setAttendanceError('');
    setAttendanceMessage('');
    try {
      console.info('[attendance][frontend][employee-timeout] request', { log_date: log_date || null, log_time: log_time || null });
      const res = await axios.post(`${API_BASE}/api/attendance/time-out`, {}, { withCredentials: true });
      const payload = res?.data || {};
      console.info('[attendance][frontend][employee-timeout] response', payload);

      // Reuse the same logic as time-in to handle payrollPreview etc.
      handleTimeInResponse(payload);

      if (payload.ok === false) {
        setAttendanceError(payload.message || 'Failed to record time-out');
      } else {
        setAttendanceMessage(payload.message || 'Time-out recorded');
        setHasOpenShift(false);
        setIsOnBreak(false);
        setOpenShiftRecord(null);
      }
      try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
        // notify payroll refresh
        try { window.dispatchEvent(new Event('payrollUpdated')); } catch (e) {}
      if (payload.ok !== false) {
        await fetchRecentAttendance();
        console.info('[attendance][frontend][employee-timeout] refreshed');
      }
    } catch (err) {
      console.error('markTimeOutEmployee error', err);
      setAttendanceError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'Failed to record time-out');
    } finally {
      setAttendanceLoading(false);
      setTimeOutDialogOpen(false);
    }
  };

  const startBreakEmployee = async () => {
    if (!hasOpenShift) {
      setAttendanceError('Time in first before starting your break.');
      return;
    }
    setAttendanceLoading(true);
    setAttendanceError('');
    setAttendanceMessage('');
    try {
      const res = await axios.post(`${API_BASE}/api/attendance/break/start`, {}, { withCredentials: true });
      setIsOnBreak(true);
      setAttendanceMessage(res?.data?.message || 'Break started.');
      await fetchRecentAttendance();
    } catch (err) {
      setAttendanceError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to start break.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const endBreakEmployee = async () => {
    if (!hasOpenShift) {
      setAttendanceError('No open shift found.');
      return;
    }
    setAttendanceLoading(true);
    setAttendanceError('');
    setAttendanceMessage('');
    try {
      const res = await axios.post(`${API_BASE}/api/attendance/break/end`, {}, { withCredentials: true });
      setIsOnBreak(false);
      setAttendanceMessage(res?.data?.message || 'Break ended.');
      await fetchRecentAttendance();
    } catch (err) {
      setAttendanceError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to end break.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = '/dashboard/employee';
  const openInventoryFromAlert = React.useCallback((item) => {
    const itemName = String(item?.item_name || '').trim();
    if (itemName) setInventorySearchTerm(itemName);
    navigate(`${basePath}?section=inventory`);
    setActiveSection('inventory');
  }, [basePath, navigate]);

  const attendanceRevisionSections = React.useMemo(
    () => new Set(['home', 'attendance-record', 'dtr-photos']),
    []
  );
  const normalizeVisibleSection = React.useCallback(
    (section) => (attendanceRevisionSections.has(section) ? section : 'home'),
    [attendanceRevisionSections]
  );

  useEffect(() => {
    if (mustChangePassword) return;
    const section = new URLSearchParams(location.search || '').get('section');
    const nextSection = normalizeVisibleSection(section || activeSection);
    if (nextSection !== activeSection) setActiveSection(nextSection);
    if (section && nextSection !== section) {
      try { navigate(`${basePath}?section=${nextSection}`, { replace: true }); } catch (e) {}
    }
  }, [location.search, activeSection, mustChangePassword, normalizeVisibleSection, navigate, basePath]);

  useEffect(() => {
    if (mustChangePassword) return;
    const target = `${basePath}?section=${activeSection}`;
    const current = `${location.pathname}${location.search || ''}`;
    if (!location.search && location.pathname === basePath) {
      if (current !== target) {
        try { navigate(target, { replace: true }); } catch (e) {}
      }
    }
  }, [location.search, location.pathname, activeSection, mustChangePassword, navigate]);

  const menuItems = mustChangePassword ? ([
    { id: "change-password", label: "Change Password", icon: <AssignmentIcon />, route: '/change-password' },
  ]) : ([
    { id: "home", label: "Home", icon: <HomeIcon />, route: `${basePath}?section=home` },
    { id: "attendance-record", label: "Attendance Record", icon: <EventNoteIcon />, route: `${basePath}?section=attendance-record` },
    { id: "dtr-photos", label: "Attendance Evidence / DTR Photos", icon: <AssignmentIcon />, route: `${basePath}?section=dtr-photos` },
  ]);

  const navMenuLabel = (item) => {
    if (item.id === 'inbox') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>Inbox</span>
          <Badge color="error" badgeContent={unreadInboxCount} max={99} invisible={!unreadInboxCount} />
        </Box>
      );
    }
    if (item.id === 'inventory-request') {
      return 'Inventory Request';
    }
    return item.label;
  };

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const renderSidebarContent = (isMobile = false) => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', overflowY: 'auto' }}>
      <Box>
        <Box sx={{ p: 1, mb: 1, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                bgcolor: "rgba(255,255,255,0.2)",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <img
                src="/ezra-logo.png"
                alt="Ezra Logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                EZRA GENERAL
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                SERVICES
              </Typography>
            </Box>
          </Box>
        </Box>

        <List sx={{ px: 0 }}>
          {menuItems.map((item) => (
            <SidebarNavItem
              key={item.id}
              label={navMenuLabel(item)}
              icon={item.icon}
              to={item.route || basePath}
              exact
              onClick={() => {
                setActiveSection(item.id);
                if (isMobile) setMobileOpen(false);
              }}
            />
          ))}
        </List>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Divider sx={{ bgcolor: '#ffffff33', mb: 2 }} />
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, cursor: 'pointer' }}
          onClick={() => {
            setOpenProfileDialog(true);
            if (isMobile) setMobileOpen(false);
          }}
        >
          <Avatar sx={{ bgcolor: '#e9eef9', color: '#2f74d0' }}>{(user && user.firstname ? user.firstname[0] : 'E')}</Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{user ? (fullDisplayName || 'Employee') : 'Employee'}</Typography>
            <Typography variant="caption">{user && (user.employeeid || user.username) ? String(user.employeeid || user.username).padStart(8,'0') : ''}</Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => {
            if (isMobile) setMobileOpen(false);
            onLogout();
          }}
          fullWidth
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "home":
        return (
          <Box>
            <Card className="bubble-card" sx={{ mb: 3, borderRadius: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                    <Typography variant="h4" sx={{ color: "#1e3a8a", fontWeight: 600 }}>
                      Hi {fullDisplayName || (user?.username || "Employee")}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#1e40af", mt: 1 }}>
                      Welcome to your Employee Dashboard
                    </Typography>
                  </Box>
                </Box>
                {/* Employee kiosk/break/time-out actions */}
                {normalizedRole === 'employee' && (
                  <Box sx={{ mt: 3, display: 'grid', gap: 2 }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isOnBreak ? '#bfdbfe' : '#dbeafe',
                        bgcolor: isOnBreak ? '#eff6ff' : '#f8fbff'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 220 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                            {!hasOpenShift ? 'Waiting for entrance kiosk time in' : (isOnBreak ? 'Currently on break' : 'Shift is active')}
                          </Typography>
                          {isOnBreak && (
                            <Typography variant="body2" sx={{ color: '#475569', mt: 0.5 }}>
                              {`Break started at ${breakStartLabel}. Return before ${breakReturnLabel}.`}
                            </Typography>
                          )}
                        </Box>
                        {isOnBreak && (
                          <Box
                            sx={{
                              minWidth: 170,
                              px: 2,
                              py: 1.25,
                              borderRadius: 2,
                              bgcolor: '#0f172a',
                              color: '#fff',
                              textAlign: 'center'
                            }}
                          >
                            <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', letterSpacing: 0.6 }}>
                              BREAK COUNTDOWN
                            </Typography>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1.15, fontFamily: 'monospace' }}>
                              {breakCountdownLabel}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          variant="contained"
                          color={isOnBreak ? 'info' : 'secondary'}
                          disabled={!hasOpenShift || attendanceLoading}
                          onClick={() => {
                            setAttendanceError('');
                            setAttendanceMessage('');
                            if (isOnBreak) endBreakEmployee();
                            else startBreakEmployee();
                          }}
                        >
                          {isOnBreak ? 'End Break' : 'Start Break'}
                        </Button>
                        <Button
                          variant="contained"
                          color="warning"
                          disabled={!hasOpenShift || attendanceLoading || isOnBreak}
                          onClick={() => { setTimeOutDialogOpen(true); setAttendanceError(''); setAttendanceMessage(''); }}
                        >
                          Time Out
                        </Button>
                      </Box>
                    </Paper>
                  </Box>
                )}

                <Paper sx={{ p: 2, mt: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                      Attendance Recorder
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          value={selfAttendanceFilter.month}
                          onChange={(e) => setSelfAttendanceFilter((prev) => ({ ...prev, month: Number(e.target.value) }))}
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 92 }}>
                        <Select
                          value={selfAttendanceFilter.year}
                          onChange={(e) => setSelfAttendanceFilter((prev) => ({ ...prev, year: Number(e.target.value) }))}
                        >
                          {availableYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  {attendanceLoading ? (
                    <Typography variant="body2">Loading...</Typography>
                  ) : visibleSelfAttendanceRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No attendance records yet.
                    </Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 260 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Time In</TableCell>
                            <TableCell>Time Out</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {visibleSelfAttendanceRows.map((row, index) => (
                            <TableRow key={row.id || index}>
                              <TableCell>{formatDateOnly(row.log_date)}</TableCell>
                              <TableCell>{row.log_time ? formatTime12h(row.log_time) : '-'}</TableCell>
                              <TableCell>{row.time_out ? formatTime12h(row.time_out) : '-'}</TableCell>
                              <TableCell>{getStatusChip(getAttendanceStatusLabel(row))}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </CardContent>
            </Card>

            <Box sx={{ display: 'grid', gap: 3 }}>
              <Box>
                <Paper className="bubble-card" sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Personal Calendar
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <PersonalCalendar employeeId={user?.employeeid || user?.employee_no} />
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Box>
        );

      case "payroll":
        return (
          <Box className="print-area">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5">Payslip</Typography>
              <Button className="no-print" variant="outlined" onClick={printPayslipOnly}>
                PRINT
              </Button>
            </Box>

            {payrollLoading ? (
              <Typography>Loading payslip...</Typography>
            ) : (
              <React.Fragment>
                {payrollError && (
                  <Typography color="error">{payrollError}</Typography>
                )}
                <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', justifyContent: personalPayrollCutoffs.length > 0 ? 'flex-start' : 'space-between', gap: 1, mb: 2 }}>
                  {personalPayrollCutoffs.length > 0 ? (
                    <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 380 } }}>
                      <Select
                        value={selectedPayslipCutoffId}
                        onChange={(e) => setSelectedPayslipCutoffId(String(e.target.value || ''))}
                        displayEmpty
                      >
                        {personalPayrollCutoffs.map((cutoff) => (
                          <MenuItem key={cutoff.id} value={String(cutoff.id)}>
                            {`${formatDateOnly(cutoff.period_start)} - ${formatDateOnly(cutoff.period_end)} | Pay ${formatDateOnly(cutoff.pay_date)} | ${cutoff.status}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <React.Fragment>
                      <IconButton onClick={handlePayslipPrev} size="small">
                        <ChevronLeftIcon />
                      </IconButton>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      >
                        {(() => {
                          const bounds = getMonthBoundsForYear(selectedYear);
                          const months = [];
                          for (let m = bounds.start; m <= bounds.end; m += 1) months.push(m);
                          return months;
                        })().map((m) => (
                          <MenuItem key={m} value={m}>
                            {new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={selectedYear}
                        onChange={(e) => {
                          const y = Number(e.target.value);
                          setSelectedYear(y);
                          const bounds = getMonthBoundsForYear(y);
                          if (selectedMonth < bounds.start) setSelectedMonth(bounds.start);
                          if (selectedMonth > bounds.end) setSelectedMonth(bounds.end);
                        }}
                      >
                        {availableYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                      </Select>
                    </FormControl>
                      </Box>
                      <IconButton
                        onClick={handlePayslipNext}
                        size="small"
                        disabled={selectedYear === currentYear && selectedMonth === currentMonth}
                      >
                        <ChevronRightIcon />
                      </IconButton>
                    </React.Fragment>
                  )}
                </Box>
                {!payrollError && !currentPayslip && (
                  <Typography color="text.secondary">No payslip found for this period.</Typography>
                )}
                {!payrollError && currentPayslip && <PayslipCard payslip={currentPayslip} />}
              </React.Fragment>
            )}
          </Box>
        );

      case "inventory":
        // Employee inventory view: show available items and allow adding materials
        const openAddStockDialog = (item = null) => {
          setAddError('');
          setAddMessage('');
          setAddItemId(item ? String(item.id || item.item_name || '') : '');
          setAddQuantity(1);
          setAddDialogOpen(true);
        };
        const selectedAddItem = inventoryItems.find((item) => String(item.id || item.item_name) === String(addItemId)) || null;
        const selectedAddItemQuantity = Number(selectedAddItem?.quantity || 0);
        const incrementAddQuantity = (increment) => {
          setAddQuantity((prev) => {
            const current = Number(prev || 0);
            const safeCurrent = Number.isFinite(current) ? current : 0;
            return safeCurrent + increment;
          });
        };
        const filteredInventoryRows = inventoryItems.filter((item) =>
          String(item?.item_name || '').toLowerCase().includes(String(inventorySearchTerm || '').trim().toLowerCase())
        );
        const handleAddMaterials = async () => {
          const itemIdNum = Number(addItemId);
          if (!itemIdNum || !addQuantity || addQuantity <= 0) {
            setAddError('Please select an item and a positive quantity.');
            return;
          }
      try {
        await axios.post(`${API_BASE}/api/inventory/stocks/add`, { item_id: itemIdNum, quantity: Number(addQuantity) }, { withCredentials: true });
            setAddMessage('Materials added successfully.');
            setAddError('');
            setAddItemId('');
            setAddQuantity(1);
            setAddDialogOpen(false);
            await fetchInventoryItems();
          } catch (err) {
            console.error('Failed to add materials:', err);
            setAddError('Failed to add materials.');
          }
        };
        const handleAddItem = async () => {
          const name = String(newItemName || '').trim();
          if (!name) {
            setAddError('Please enter an item name.');
            return;
          }
          try {
            const res = await axios.post(`${API_BASE}/api/inventory/items`, { item_name: name }, { withCredentials: true });
            setNewItemName('');
            setAddMessage('Item added successfully.');
            setAddError('');
            await fetchInventoryItems();
            if (res?.data?.id) setAddItemId(String(res.data.id));
          } catch (err) {
            console.error('Failed to add item:', err);
            setAddError('Failed to add item.');
          }
        };
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Inventory
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Available inventory items.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField label="New Item" value={newItemName} onChange={(e) => setNewItemName(e.target.value)} size="small" />
                <Button variant="outlined" onClick={handleAddItem}>Add Item</Button>
              </Box>
              <Button variant="contained" onClick={() => openAddStockDialog()}>Add Stock</Button>
              {/* Employees are not allowed to request inventory items */}
              {user?.role !== "employee" && (
                <Button variant="outlined" onClick={() => { setRequestDialogOpen(true); setRequestItem(inventoryItems && inventoryItems.length ? inventoryItems[0] : null); }}>Request Item</Button>
              )}
            </Box>

            <Paper className="bubble-card" sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">Available Items</Typography>
                </Box>
                <TextField
                  size="small"
                  label="Search items"
                  value={inventorySearchTerm}
                  onChange={(e) => setInventorySearchTerm(e.target.value)}
                  placeholder="Search by item name"
                  sx={{ width: { xs: '100%', sm: 280 } }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInventoryRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                          No inventory items match your search.
                        </TableCell>
                      </TableRow>
                    ) : filteredInventoryRows.map(i => {
                      const quantity = Number(i?.quantity || 0);
                      const level = getInventoryStockLevel(quantity);
                      const status = level === 'critical' ? 'critical_stock' : (level === 'warning' ? 'low_stock' : 'available');
                      return (
                        <TableRow key={i.id || i.item_name}>
                          <TableCell>{i.item_name}</TableCell>
                          <TableCell align="right">{i.quantity}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ textTransform: 'capitalize' }}>
                              {status === 'available' && (
                                <Chip size="small" color="success" variant="outlined" label="Available" />
                              )}
                              {status === 'low_stock' && (
                                <Chip
                                  size="small"
                                  variant="filled"
                                  label="Low stock"
                                  sx={{ bgcolor: '#ed6c02', color: '#fff8e1', fontWeight: 700 }}
                                />
                              )}
                              {status === 'critical_stock' && (
                                <Chip size="small" color="error" variant="outlined" label="Critical stock" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                              <Button size="small" variant="outlined" onClick={() => openAddStockDialog(i)}>
                                Add Stock
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Add Materials Dialog */}
            <Dialog
              open={addDialogOpen}
              onClose={() => setAddDialogOpen(false)}
              fullWidth
              maxWidth="sm"
              PaperProps={{
                sx: {
                  width: 'min(92vw, 640px)',
                  maxWidth: '640px',
                  overflowX: 'hidden',
                }
              }}
            >
              <DialogTitle>Add Materials</DialogTitle>
              <DialogContent
                sx={{
                  width: '100%',
                  minWidth: 0,
                  minHeight: 260,
                  overflowX: 'hidden',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    pt: 0.5,
                  }}
                >
                  <TextField
                    select
                    label="Item"
                    fullWidth
                    margin="normal"
                    value={addItemId}
                    onChange={(e) => setAddItemId(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ m: 0, minWidth: 0 }}
                >
                  <option value=""></option>
                    {inventoryItems.map(i => (
                      <option key={i.id || i.item_name} value={i.id || i.item_name}>{i.item_name}</option>
                    ))}
                  </TextField>
                  <Box sx={{ width: '100%', minWidth: 0, minHeight: 52 }}>
                    {selectedAddItem && (
                      <Alert
                        severity="info"
                        sx={{
                          width: '100%',
                          minWidth: 0,
                          overflowX: 'hidden',
                          '& .MuiAlert-message': {
                            width: '100%',
                            minWidth: 0,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                        }}
                      >
                        Current stock for <strong>{selectedAddItem.item_name}</strong>: {selectedAddItemQuantity}
                        {' '}| New total after add: <strong>{selectedAddItemQuantity + Number(addQuantity || 0)}</strong>
                      </Alert>
                    )}
                  </Box>
                  <TextField
                    label="Quantity"
                    type="number"
                    fullWidth
                    margin="normal"
                    value={addQuantity}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setAddQuantity(Number.isFinite(next) ? next : 0);
                    }}
                    inputProps={{ min: 1 }}
                    sx={{ m: 0, minWidth: 0 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                    {[1, 5, 10, 25, 50, 100, 200, 400].map((qty) => (
                      <Button key={qty} size="small" variant="outlined" onClick={() => incrementAddQuantity(qty)}>
                        +{qty}
                      </Button>
                    ))}
                  </Box>
                  {addMessage && (
                    <Alert severity="success" sx={{ width: '100%', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {addMessage}
                    </Alert>
                  )}
                  {addError && (
                    <Alert severity="error" sx={{ width: '100%', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {addError}
                    </Alert>
                  )}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleAddMaterials}>Add</Button>
              </DialogActions>
            </Dialog>

            {/* Request Dialog */}
            <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)}>
              <DialogTitle>Request Item</DialogTitle>
              <DialogContent>
                <TextField
                  select
                  label="Item"
                  fullWidth
                  margin="normal"
                  value={requestItem ? (requestItem.id || requestItem.item_name) : ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    const it = inventoryItems.find(i => String(i.id || i.item_name) === String(id));
                    setRequestItem(it || null);
                  }}
                  SelectProps={{ native: true }}
                >
                  <option value=""></option>
                  {inventoryItems.map(i => (
                    <option key={i.id || i.item_name} value={i.id || i.item_name}>{i.item_name}{typeof i.quantity !== 'undefined' ? ` — ${i.quantity} available` : ''}</option>
                  ))}
                </TextField>
                <TextField label="Quantity" type="number" fullWidth margin="normal" value={requestQuantity} onChange={(e) => setRequestQuantity(Number(e.target.value))} inputProps={{ min: 1 }} />
                <TextField label="Note (optional)" fullWidth margin="normal" value={requestNote} onChange={(e) => setRequestNote(e.target.value)} />
                {requestError && <Alert severity="error" sx={{ mt: 1 }}>{requestError}</Alert>}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
                <Button variant="contained" onClick={async () => {
                  if (!requestItem || !requestQuantity || requestQuantity <= 0) { setRequestError('Please enter a valid quantity'); return; }
                  // client-side stock check: if item has a quantity field, prevent requesting more than available
                  const available = Number(requestItem?.quantity ?? 0);
                  if (available > 0 && Number(requestQuantity) > available) {
                    setRequestError(`Requested quantity (${requestQuantity}) exceeds available stock (${available}).`);
                    return;
                  }
                  try {
                    const payload = { quantity: Number(requestQuantity), note: requestNote, notes: requestNote, message: requestNote };
                    if (requestItem.id) payload.item_id = requestItem.id;
                    else payload.item_name = requestItem.item_name;
                    await axios.post(`${API_BASE}/api/inventory/requests`, payload, { withCredentials: true });
                    setRequestDialogOpen(false);
                    setRequestItem(null);
                    setRequestQuantity(1);
                    setRequestNote('');
                    // refresh lists
                    fetchInventoryRequests();
                    fetchInventoryItems();
                    try { window.dispatchEvent(new Event('refreshNotifications')); } catch (e) {}
                  } catch (err) {
                    console.error('Failed to create inventory request', err);
                    setRequestError('Failed to create request.');
                  }
                }}>Request</Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case "inventory-request":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              Inventory Requests
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Review and approve inventory requests from custodians.
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchInventoryRequests}
              sx={{ mb: 2 }}
            >
              Refresh
            </Button>
            {inventoryLoading && <Typography>Loading inventory requests...</Typography>}
            {inventoryError && <Typography color="error">{inventoryError}</Typography>}
            {!inventoryLoading && !inventoryError && inventoryRequests.length === 0 && (
              <Typography>No inventory requests found.</Typography>
            )}
            {!inventoryLoading && !inventoryError && inventoryRequests.length > 0 && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Requested By</TableCell>
                      <TableCell>Work Site</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Date Requested</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.item_name || request.itemName}</TableCell>
                        <TableCell>{request.requested_quantity || request.quantity}</TableCell>
                        <TableCell>
                          {(() => {
                            const name = request.requested_by_name || request.requested_by || request.username || '';
                            const empNo = request.requested_by_employee_no || '';
                            return empNo ? `${empNo} - ${name}` : name;
                          })()}
                        </TableCell>
                        <TableCell>{request.branch_code && request.branch_name ? `${request.branch_code} - ${request.branch_name}` : request.branch_code || request.branch_name || request.requested_by_branch_name || '-'}</TableCell>
                        <TableCell>{request.notes || request.message || request.note || '-'}</TableCell>
                        <TableCell>{new Date(request.requested_at || request.date_requested || request.created_at || Date.now()).toLocaleDateString()}</TableCell>
                        <TableCell>{request.status}</TableCell>
                          <TableCell>
                            {request.status === 'pending' && normalizedRole === 'employee' ? (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleApproveInventory(request.id)}
                                  sx={{ mr: 1 }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={() => handleRejectInventory(request.id)}
                                >
                                  Reject
                                </Button>
                              </>
                            ) : (
                              <Typography variant="caption">{request.status || 'pending'}</Typography>
                            )}
                          </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case "attendance-record":
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                  Attendance Record
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View attendance records for DTR validation.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={() => fetchCompanyAttendance({ month: companyAttendanceFilters.month, year: companyAttendanceFilters.year })}>Refresh</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={companyAttendanceFilters.month}
                  onChange={(e) => {
                    const month = Number(e.target.value);
                    setCompanyAttendanceFilters((prev) => ({ ...prev, month }));
                    fetchCompanyAttendance({ month, year: companyAttendanceFilters.year });
                  }}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-US', { month: 'long' })}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 92 }}>
                <Select
                  value={companyAttendanceFilters.year}
                  onChange={(e) => {
                    const year = Number(e.target.value);
                    setCompanyAttendanceFilters((prev) => ({ ...prev, year }));
                    fetchCompanyAttendance({ month: companyAttendanceFilters.month, year });
                  }}
                >
                  {availableYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={companyAttendanceFilters.role}
                  onChange={(e) => setCompanyAttendanceFilters((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="hr">HR</MenuItem>
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="custodian">Custodian</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={companyAttendanceFilters.status}
                  onChange={(e) => setCompanyAttendanceFilters((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="present">Present</MenuItem>
                  <MenuItem value="absent">Absent</MenuItem>
                  <MenuItem value="half_day">Half Day</MenuItem>
                  <MenuItem value="leave">Leave</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={companyAttendanceFilters.method}
                  onChange={(e) => setCompanyAttendanceFilters((prev) => ({ ...prev, method: e.target.value }))}
                >
                  <MenuItem value="all">All Methods</MenuItem>
                  <MenuItem value="face">Face</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="leave">Leave Request</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Employee ID"
                value={companyAttendanceFilters.employeeId}
                onChange={(e) => setCompanyAttendanceFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
                placeholder="00000010"
                sx={{ width: { xs: '100%', sm: 170 } }}
              />
            </Box>
            {companyAttendanceError && <Alert severity="error" sx={{ mb: 2 }}>{companyAttendanceError}</Alert>}
            {companyAttendanceLoading ? (
              <Typography>Loading attendance records...</Typography>
            ) : visibleCompanyAttendanceRows.length === 0 ? (
              <Typography color="text.secondary">No attendance records found.</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 540 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Name</TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time In</TableCell>
                      <TableCell>Time Out</TableCell>
                      <TableCell>Break Start</TableCell>
                      <TableCell>Break End</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleCompanyAttendanceRows.map((row, index) => (
                      <TableRow key={row.id || index}>
                        <TableCell>{formatName(row) || row.employee_name || row.username || '-'}</TableCell>
                        <TableCell>{String(row.employeeid || row.employee_no || '').padStart(8, '0')}</TableCell>
                        <TableCell>{formatDateOnly(row.log_date || row.date)}</TableCell>
                        <TableCell>{row.log_time ? formatTime12h(row.log_time) : '-'}</TableCell>
                        <TableCell>{row.time_out ? formatTime12h(row.time_out) : '-'}</TableCell>
                        <TableCell>{row.break_start ? formatTime12h(row.break_start) : '-'}</TableCell>
                        <TableCell>{row.break_end ? formatTime12h(row.break_end) : '-'}</TableCell>
                        <TableCell>{getAttendanceMethodLabel(row)}</TableCell>
                        <TableCell>{getStatusChip(getAttendanceStatusLabel(row))}</TableCell>
                        <TableCell align="right">
                          {(() => {
                            const hours = Number(row.hours_worked ?? row.hours ?? 0);
                            return Number.isFinite(hours) ? hours.toFixed(2) : '0.00';
                          })()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case "dtr-photos":
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                  Attendance Evidence / DTR Photos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare each attendance evidence photo against the Attendance Record, then mark it clean or needs edit for HR final action.
                </Typography>
              </Box>
              <Button variant="outlined" onClick={fetchDtrPhotos}>Refresh</Button>
            </Box>
            {dtrPhotosError && <Alert severity="error" sx={{ mb: 2 }}>{dtrPhotosError}</Alert>}
            {dtrPhotosLoading ? (
              <Typography>Loading attendance evidence...</Typography>
            ) : dtrPhotos.length === 0 ? (
              <Typography color="text.secondary">No attendance evidence uploaded yet.</Typography>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Photo</TableCell>
                      <TableCell>Custodian</TableCell>
                      <TableCell>Employee ID</TableCell>
                      <TableCell>Uploaded At</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell>Employee Review</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dtrPhotos.map((row) => {
                      const custodianName = formatName(row) || row.username || '-';
                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            {row.file_url ? (
                              <Box component="img" src={row.file_url} alt="DTR evidence" sx={{ width: 80, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid #dbe3ef' }} />
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>{custodianName}</TableCell>
                          <TableCell>{String(row.employeeid || '').padStart(8, '0')}</TableCell>
                          <TableCell>{row.captured_at ? formatDateTimePH(row.captured_at) : '-'}</TableCell>
                          <TableCell>{row.notes || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'grid', gap: 0.5 }}>
                              {renderDtrReviewChip(row.employee_review_status)}
                              {getDtrReviewerLabel(row) && (
                                <Typography variant="caption" color="text.secondary">{getDtrReviewerLabel(row)}</Typography>
                              )}
                              {row.employee_review_notes && (
                                <Typography variant="caption" color="text.secondary">{row.employee_review_notes}</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                              {row.file_url && (
                                <Button size="small" variant="outlined" component="a" href={row.file_url} target="_blank" rel="noreferrer">
                                  View Photo
                                </Button>
                              )}
                              <Button size="small" variant="outlined" onClick={() => openDtrCompare(row)}>
                                Compare
                              </Button>
                              <Button size="small" variant="contained" color="success" onClick={() => openDtrReview(row, 'validated')}>
                                Clean
                              </Button>
                              <Button size="small" variant="contained" color="warning" onClick={() => openDtrReview(row, 'needs_edit')}>
                                Needs Edit
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case "file-leave":
        return (
          <Box>
            <Typography variant="h5" gutterBottom>
              File Leave
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Request time off and manage your leave.
            </Typography>

            {/* Leave Request Form */}
            <Box sx={{ display: "grid", gap: 3 }}>
              <LeaveRequestForm
                onSubmitted={fetchLeaveRequests}
                onMetaLoaded={handleLeaveMetaLoaded}
                enabled={!mustChangePassword}
              />

              <LeaveBalancesSummary
                balances={leaveBalances}
                leaveTypes={allowedLeaveTypes}
              />

            {/* Leave Requests History */}
            <Paper className="bubble-card" sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>
                Your Leave Requests
              </Typography>
              <LeaveRequestsTable
                rows={leaveRequests}
                loading={leaveLoading}
                error={leaveError || ""}
                allowFollowupUpload
                onAttachmentUploaded={fetchLeaveRequests}
              />
              {false && leaveLoading && <Typography>Loading leave requests...</Typography>}
              {false && leaveError && <Typography color="error">{leaveError}</Typography>}
              {false && !leaveLoading && !leaveError && leaveRequests.length === 0 && (
                <Typography>No leave requests found.</Typography>
              )}
              {false && !leaveLoading && !leaveError && leaveRequests.length > 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Leave Type</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Date Requested</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Attachment</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaveRequests.map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>{request.leave_type}</TableCell>
                          <TableCell>{formatDatePH(request.start_date)}</TableCell>
                          <TableCell>{formatDatePH(request.end_date)}</TableCell>
                          <TableCell>{request.date_submitted ? formatDateTimePH(request.date_submitted) : (request.date_requested ? formatDateTimePH(request.date_requested) : '–')}</TableCell>
                          <TableCell>{request.reason}</TableCell>
                          <TableCell>
                            {request.file_url ? (
                              <a href={request.file_url} target="_blank" rel="noreferrer">View</a>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{request.status}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
            </Box>
          </Box>
        );

      case "inbox":
        return (
          <InboxPanel onUnreadCountChange={setUnreadInboxCount} />
        );

      default:
        return null;
    }
  };

  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <Box sx={{
      display: "flex",
      flexDirection: { xs: 'column', md: 'row' },
      height: { xs: 'auto', md: '100vh' },
      bgcolor: "#f0f4ff",
      overflowX: 'hidden',
      "& .MuiButton-contained:not(.MuiButton-containedError)": {
        bgcolor: "#2a3f9f",
        "&:hover": { bgcolor: "#22367f" }
      }
    }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-area { padding: 0 !important; margin: 0 !important; }
          body { background: #fff; }
        }
      `}</style>
      {/* Sidebar */}
      <Box
        className="dashboard-sidebar no-print"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: drawerWidth,
          flex: `0 0 ${drawerWidth}px`,
          bgcolor: "#3b63b8",
          color: "white",
          flexDirection: "column",
          minHeight: '100vh',
          overflow: 'hidden'
        }}
      >
        {renderSidebarContent(false)}
      </Box>

      <Drawer
        className="no-print"
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            bgcolor: '#3b63b8',
            color: 'white',
            boxSizing: 'border-box',
          }
        }}
      >
        {renderSidebarContent(true)}
      </Drawer>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Top Header */}
        <Box
          className="dashboard-header no-print"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: { xs: 2, md: 3 },
            bgcolor: "white",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, color: "#183152" }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ color: "#183152", fontWeight: 500 }}>
              {menuItems.find(item => item.id === activeSection)?.label || "Dashboard"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <LiveDateTimeWidget />
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, cursor: 'pointer' }}
              onClick={() => setOpenProfileDialog(true)}
            >
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {user?.username || "Employee"}
              </Typography>
              <Avatar sx={{ width: 32, height: 32, bgcolor: "#2f74d0" }}>
                <PersonIcon sx={{ fontSize: 18 }} />
              </Avatar>
            </Box>
          </Box>
        </Box>

        {/* Content Area */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, bgcolor: "#f0f7ff", overflow: "auto" }}>
          {alreadyTimedInBanner && (
            <Box
              sx={{
                mb: 1,
                p: '8px 16px',
                bgcolor: '#FFF8E1',
                borderRadius: 1,
                color: '#8D6E63',
                border: '1px solid #FFE082',
                fontSize: 14
              }}
            >
              You already timed in and haven't timed out yet. Please time out before recording another time in.
            </Box>
          )}
          {location?.pathname === '/dashboard/employee' ? renderContent() : <Outlet />}
        </Box>
        {/* Attendance dialogs (employee break and time-out) */}
        <Dialog open={timeOutDialogOpen} onClose={() => setTimeOutDialogOpen(false)}>
          <DialogTitle>Confirm Time Out</DialogTitle>
          <DialogContent>
            <Typography>You're about to Time Out as <strong>{user?.username}</strong>.</Typography>
            <Typography sx={{ mt: 1 }}>Time: {formatDateTimeToTime12h(new Date())}</Typography>
            {attendanceMessage && <Alert severity="success" sx={{ mt: 2 }}>{attendanceMessage}</Alert>}
            {attendanceError && <Alert severity="error" sx={{ mt: 2 }}>{attendanceError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTimeOutDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={attendanceLoading || !hasOpenShift || isOnBreak} onClick={() => markTimeOutEmployee() }>
              {attendanceLoading ? 'Recording...' : 'Confirm Time Out'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={normalizedRole === 'employee' && isOnBreak}
          disableEscapeKeyDown
          fullWidth
          maxWidth="sm"
          onClose={() => {}}
        >
          <DialogTitle sx={{ pb: 1 }}>Currently On Break</DialogTitle>
          <DialogContent>
            <Alert severity={breakCountdownMs > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
              Your account is paused while you are on break. End your break first before using the rest of your account.
            </Alert>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: '#0f172a',
                color: '#fff',
                textAlign: 'center'
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', color: '#cbd5e1', letterSpacing: 0.8 }}>
                BREAK COUNTDOWN
              </Typography>
              <Typography sx={{ fontSize: { xs: 30, sm: 40 }, fontWeight: 800, lineHeight: 1.1, fontFamily: 'monospace', mt: 0.5 }}>
                {breakCountdownLabel}
              </Typography>
            </Box>
            <Box sx={{ mt: 2, display: 'grid', gap: 0.75 }}>
              <Typography variant="body2"><strong>Break started:</strong> {breakStartLabel}</Typography>
              <Typography variant="body2"><strong>Expected return:</strong> {breakReturnLabel}</Typography>
              <Typography variant="body2" color="text.secondary">
                {breakCountdownMs > 0
                  ? 'End your break when you return to work.'
                  : 'Your one-hour break window is over. End your break now to continue working.'}
              </Typography>
              {attendanceError && <Alert severity="error" sx={{ mt: 1 }}>{attendanceError}</Alert>}
              {attendanceMessage && <Alert severity="success" sx={{ mt: 1 }}>{attendanceMessage}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button variant="contained" disabled={attendanceLoading} onClick={() => endBreakEmployee()}>
              {attendanceLoading ? 'Ending Break...' : 'End Break'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dtrCompareDialog.open}
          onClose={() => setDtrCompareDialog({ open: false, row: null, rows: [], loading: false, error: '', month: null, year: null })}
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle>DTR Photo Comparison</DialogTitle>
          <DialogContent>
            {dtrCompareDialog.row && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '420px 1fr' }, gap: 2, pt: 1 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>Uploaded DTR Photo</Typography>
                  {dtrCompareDialog.row.file_url ? (
                    <Box
                      component="img"
                      src={dtrCompareDialog.row.file_url}
                      alt="DTR evidence"
                      sx={{ width: '100%', maxHeight: 520, objectFit: 'contain', borderRadius: 2, border: '1px solid #dbe3ef', bgcolor: '#f8fafc' }}
                    />
                  ) : (
                    <Alert severity="warning">No photo file is available for this DTR evidence.</Alert>
                  )}
                  <Box sx={{ mt: 1.5, display: 'grid', gap: 0.5 }}>
                    <Typography variant="body2"><strong>Custodian:</strong> {formatName(dtrCompareDialog.row) || dtrCompareDialog.row.username || '-'}</Typography>
                    <Typography variant="body2"><strong>Employee ID:</strong> {String(dtrCompareDialog.row.employeeid || '').padStart(8, '0')}</Typography>
                    <Typography variant="body2"><strong>Uploaded:</strong> {dtrCompareDialog.row.captured_at ? formatDateTimePH(dtrCompareDialog.row.captured_at) : '-'}</Typography>
                    <Typography variant="body2"><strong>Notes:</strong> {dtrCompareDialog.row.notes || '-'}</Typography>
                  </Box>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>Matching Attendance Record</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getMonthName(dtrCompareDialog.month)} {dtrCompareDialog.year} - filtered to this custodian.
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => shiftDtrCompareMonth(-1)} disabled={dtrCompareDialog.loading}>
                        Previous Month
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => shiftDtrCompareMonth(1)} disabled={dtrCompareDialog.loading}>
                        Next Month
                      </Button>
                    </Box>
                  </Box>
                  {dtrCompareDialog.error && <Alert severity="error" sx={{ mb: 1 }}>{dtrCompareDialog.error}</Alert>}
                  {dtrCompareDialog.loading ? (
                    <Typography>Loading matching attendance...</Typography>
                  ) : dtrCompareDialog.rows.length === 0 ? (
                    <Typography color="text.secondary">No matching attendance rows found for this month.</Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 520 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Time In</TableCell>
                            <TableCell>Time Out</TableCell>
                            <TableCell>Break Start</TableCell>
                            <TableCell>Break End</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Hours</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dtrCompareDialog.rows.map((row, index) => (
                            <TableRow key={row.id || index}>
                              <TableCell>{formatDateOnly(row.log_date || row.date)}</TableCell>
                              <TableCell>{row.log_time ? formatTime12h(row.log_time) : '-'}</TableCell>
                              <TableCell>{row.time_out ? formatTime12h(row.time_out) : '-'}</TableCell>
                              <TableCell>{row.break_start ? formatTime12h(row.break_start) : '-'}</TableCell>
                              <TableCell>{row.break_end ? formatTime12h(row.break_end) : '-'}</TableCell>
                              <TableCell>{getAttendanceMethodLabel(row)}</TableCell>
                              <TableCell>{getStatusChip(getAttendanceStatusLabel(row))}</TableCell>
                              <TableCell align="right">
                                {(() => {
                                  const hours = Number(row.hours_worked ?? row.hours ?? 0);
                                  return Number.isFinite(hours) ? hours.toFixed(2) : '0.00';
                                })()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDtrCompareDialog({ open: false, row: null, rows: [], loading: false, error: '', month: null, year: null })}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={dtrReviewDialog.open}
          onClose={() => {
            if (!dtrReviewDialog.saving) {
              setDtrReviewDialog({ open: false, row: null, status: '', notes: '', saving: false, error: '' });
            }
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>DTR Employee Validation</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
              <Alert severity={dtrReviewDialog.status === 'validated' ? 'success' : (dtrReviewDialog.status === 'needs_edit' ? 'warning' : 'error')}>
                You are marking this DTR photo as: <strong>{getDtrReviewLabel(dtrReviewDialog.status)}</strong>
              </Alert>
              {dtrReviewDialog.row && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                  {dtrReviewDialog.row.file_url && (
                    <Box
                      component="img"
                      src={dtrReviewDialog.row.file_url}
                      alt="DTR evidence"
                      sx={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #dbe3ef' }}
                    />
                  )}
                  <Box>
                    <Typography variant="body2"><strong>Custodian:</strong> {formatName(dtrReviewDialog.row) || dtrReviewDialog.row.username || '-'}</Typography>
                    <Typography variant="body2"><strong>Employee ID:</strong> {String(dtrReviewDialog.row.employeeid || '').padStart(8, '0')}</Typography>
                    <Typography variant="body2"><strong>Uploaded:</strong> {dtrReviewDialog.row.captured_at ? formatDateTimePH(dtrReviewDialog.row.captured_at) : '-'}</Typography>
                  </Box>
                </Box>
              )}
              <TextField
                label={dtrReviewDialog.status === 'validated' ? 'Notes (optional)' : 'Notes (required)'}
                multiline
                minRows={3}
                value={dtrReviewDialog.notes}
                onChange={(e) => setDtrReviewDialog((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder={
                  dtrReviewDialog.status === 'needs_edit'
                    ? 'Explain what attendance entry HR needs to edit.'
                    : dtrReviewDialog.status === 'rejected'
                      ? 'Explain why this DTR photo should be rejected.'
                      : 'Optional validation note.'
                }
              />
              {dtrReviewDialog.error && <Alert severity="error">{dtrReviewDialog.error}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={dtrReviewDialog.saving}
              onClick={() => setDtrReviewDialog({ open: false, row: null, status: '', notes: '', saving: false, error: '' })}
            >
              Cancel
            </Button>
            <Button variant="contained" disabled={dtrReviewDialog.saving} onClick={submitDtrReview}>
              {dtrReviewDialog.saving ? 'Saving...' : 'Submit Validation'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={false} onClose={() => {}} fullWidth maxWidth="sm">
          <DialogTitle>Updated Payslip Preview</DialogTitle>
          <DialogContent>
            {payrollPreview ? (
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography>Month/Year: {payrollPreview.month}/{payrollPreview.year}</Typography>
                <Typography>Base Salary: ₱{Number(payrollPreview.baseSalary || 0).toLocaleString()}</Typography>
                <Typography>Total Hours: {Number(payrollPreview.totalHours || 0).toFixed(2)}</Typography>
                <Typography>Overtime Hours: {Number(payrollPreview.overtimeHours || 0).toFixed(2)}</Typography>
                <Typography>Gross Pay: ₱{Number(payrollPreview.gross || 0).toLocaleString()}</Typography>
                <Divider />
                <Typography variant="subtitle2">Deductions</Typography>
                {user?.sss_no && <Typography>SSS: ₱{Number(payrollPreview.deductions?.sss || 0).toLocaleString()}</Typography>}
                {user?.philhealth_no && <Typography>PhilHealth: ₱{Number(payrollPreview.deductions?.philhealth || 0).toLocaleString()}</Typography>}
                {user?.pagibig_no && <Typography>Pag-IBIG: ₱{Number(payrollPreview.deductions?.pagibig || 0).toLocaleString()}</Typography>}
                <Typography>Late/Other: ₱{Number(payrollPreview.deductions?.late || 0).toLocaleString()}</Typography>
                <Divider />
                <Typography variant="h6">Net Salary: ₱{Number(payrollPreview.net || 0).toLocaleString()}</Typography>
              </Box>
            ) : (
              <Typography>No payslip preview available.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {}}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
      {/* Profile dialog (read-only) */}
      <MyProfileDialog open={openProfileDialog} onClose={() => setOpenProfileDialog(false)} user={user} />
    </Box>
  );
}

export default EmployeeDashboard;
